import { useState } from 'react';
import { X, Save } from 'lucide-react';
import type { Prospect } from '../services/prospectingService';

interface Props {
  prospect: Prospect;
  onSave: (fields: Partial<Prospect>) => void;
  onClose: () => void;
}

export default function ProspectEditModal({ prospect: p, onSave, onClose }: Props) {
  const [fields, setFields] = useState({
    companyName: p.companyName,
    domain: p.domain || '',
    region: p.region || '',
    keyContactName: p.keyContactName || '',
    keyContactEmail: p.keyContactEmail || '',
    keyContactPhone: p.keyContactPhone || '',
    keyContactTitle: p.keyContactTitle || '',
    keyContactDept: p.keyContactDept || '',
    winTheme: p.winTheme || '',
    outreachEmailSubj: p.outreachEmailSubj || '',
  });

  const input = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors';
  const label = 'block text-xs font-medium text-muted-foreground mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold text-sm">Edit Prospect — {p.companyName}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-lg transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Company Name</label>
              <input className={input} value={fields.companyName}
                onChange={e => setFields(p => ({ ...p, companyName: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Domain</label>
              <input className={input} value={fields.domain} placeholder="acme.com"
                onChange={e => setFields(p => ({ ...p, domain: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Region</label>
              <input className={input} value={fields.region}
                onChange={e => setFields(p => ({ ...p, region: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Contact Name</label>
              <input className={input} value={fields.keyContactName}
                onChange={e => setFields(p => ({ ...p, keyContactName: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Contact Email <span className="text-primary text-xs font-normal">(required to approve)</span></label>
              <input className={input} type="email" value={fields.keyContactEmail}
                placeholder="contact@company.com"
                onChange={e => setFields(p => ({ ...p, keyContactEmail: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Contact Phone</label>
              <input className={input} type="tel" value={fields.keyContactPhone}
                placeholder="+91 98765 43210"
                onChange={e => setFields(p => ({ ...p, keyContactPhone: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Contact Title</label>
              <input className={input} value={fields.keyContactTitle}
                onChange={e => setFields(p => ({ ...p, keyContactTitle: e.target.value }))} />
            </div>
            <div>
              <label className={label}>Department</label>
              <input className={input} value={fields.keyContactDept}
                onChange={e => setFields(p => ({ ...p, keyContactDept: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className={label}>Win Theme</label>
            <textarea rows={3} className={input} value={fields.winTheme}
              onChange={e => setFields(p => ({ ...p, winTheme: e.target.value }))} />
          </div>
          <div>
            <label className={label}>Email Subject</label>
            <input className={input} value={fields.outreachEmailSubj}
              onChange={e => setFields(p => ({ ...p, outreachEmailSubj: e.target.value }))} />
          </div>
        </div>

        <div className="flex gap-2 p-4 border-t border-border">
          <button
            onClick={() => onSave(Object.fromEntries(
              Object.entries(fields).map(([k, v]) => [k, v || null])
            ) as Partial<Prospect>)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Save className="h-4 w-4" />Save & Stage for Approval
          </button>
          <button onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
